import { z } from "zod";
import { id, instantDateTime } from "./common";
import { scheduleV1 } from "./schedule";

export const contractKind = z.enum(["rental", "reservation"]);
export const contractStatus = z.enum(["draft", "sent", "viewed", "partially_signed", "client_signed", "countersigned", "voided", "expired"]);
export const documentHash = z.string().regex(/^[a-f0-9]{64}$/, "The document version is invalid. Reload the agreement.");
export const documentToken = z.string().regex(/^[a-f0-9]{64}$/);
export const contractRequestId = z.string().regex(/^[A-Za-z0-9_-]{16,128}$/, "Use a valid request identifier.");
export const contractSignerInput = z.object({
  contactId: id,
  name: z.string().trim().min(1, "Enter the signer's name.").max(160),
  title: z.string().trim().max(160).nullable().optional(),
  signingAs: z.enum(["individual", "company"]),
  channels: z.array(z.enum(["email", "sms"])).min(1, "Choose at least one delivery method.").max(2)
    .refine(channels => new Set(channels).size === channels.length, "Choose each delivery method once."),
}).strict();
export const contractPreviewInput = z.object({
  kind: contractKind,
  signers: z.array(contractSignerInput).min(1).max(2)
    .refine(signers => new Set(signers.map(signer => signer.contactId)).size === signers.length, "Choose each signer once."),
}).strict();
export const contractSendInput = contractPreviewInput.extend({
  clientRequestId: contractRequestId,
  expectedScheduleRevision: z.number().int().nonnegative(),
  expectedSourceHash: documentHash,
}).strict();
export const contractRemindInput = z.object({ clientRequestId: contractRequestId }).strict();
export const contractVoidInput = z.object({ reason: z.string().trim().min(1, "Explain why the agreement is being voided.").max(1000) }).strict();
export const contractCountersignInput = z.object({
  typedName: z.string().trim().min(1, "Enter your signature.").max(160),
  title: z.string().trim().max(160).nullable().optional(),
  expectedHtmlSha256: documentHash,
}).strict();
export const contractPublicSignInput = z.object({
  signerToken: documentToken,
  expectedHtmlSha256: documentHash,
  typedName: z.string().trim().min(1, "Enter your full name.").max(160),
  consent: z.literal(true, "Consent is required to sign electronically."),
  consentVersion: z.literal("esign-v1"),
  exhibitAcknowledged: z.literal(true, "Please acknowledge Exhibit A."),
  signaturePng: z.string().max(280_000).regex(/^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/).optional(),
}).strict();
export const contractPublicDeclineInput = z.object({
  signerToken: documentToken,
  expectedHtmlSha256: documentHash,
  reason: z.string().trim().min(1, "Please give a reason.").max(1000),
}).strict();
export type ContractPreviewInput = z.infer<typeof contractPreviewInput>;
export type ContractSendInput = z.infer<typeof contractSendInput>;
export type ContractSignerInput = z.infer<typeof contractSignerInput>;
export type ContractPublicSignInput = z.infer<typeof contractPublicSignInput>;

/** Persisted audit shapes have no command defaults or transforms. Identity and
 * schedule structure are checked here; PDF/storage bytes remain separate. */
export const contractRecord = z.object({
  companyId: id, bookingId: id, templateId: id, kind: contractKind,
  version: z.number().int().positive(), status: contractStatus,
  clientRequestId: contractRequestId, sourceHash: documentHash, htmlSha256: documentHash,
  sentAt: instantDateTime.nullable(), expiresAt: instantDateTime.nullable(),
  countersignedAt: instantDateTime.nullable(), countersignMemberId: id.nullable(),
  pdfSha256: documentHash.nullable(),
});
export const contractSnapshotIdentity = z.object({
  version: z.literal(1), kind: contractKind, companyId: id, bookingId: id,
  contractId: id, contractVersion: z.number().int().positive(), generatedAt: instantDateTime,
  template: z.object({ key: z.enum(["rental-v1", "reservation-v1"]), version: z.number().int().positive(), sourceSha256: documentHash }),
  event: z.object({ schedule: scheduleV1, scheduleRevision: z.number().int().nonnegative() }),
});
